import { useEffect, useRef, useState } from 'react'

import { Box, TextField } from '@mui/material'
import { ReactComponent as DeviationLogo } from '../../assets/deviation_logo.svg'
import { COLORS } from '../../styles/colors'

import moment from 'moment'
import { IDataOnChangeHandlerParameters, PlanFeedFieldName } from '../../models'
import { useAppDispatch } from '../../store/hooks'
import {
	feedNewPlanFactDataOnChange,
	setNewWeightValue,
} from '../../store/newFeedPlanSlice'
import { notifyUser } from '../../store/notificationsSlice'
import { isAfterFactTime } from './feedPlanDeviation'
import { isFeedWeightMockEnabled } from './feedWeightMock'
import { TypographyPrimary } from '../../ui/typography/TypographyPrimary'

interface IPlanFeedFieldProps {
	type?: string
	maxValue?: number
	label?: string
	value?: string | number
	fieldValue?: number | null
	fieldName: PlanFeedFieldName
	groupId: number
	planId: number
	feedPlanDate: Date | null
	metaFlags: boolean
	componentTimeType?: string
	autoComplete?: string
	isFutureDate: boolean
	factDt?: number | null
	hasWeightDeviation?: boolean
}

export const PlanFeedField = (props: IPlanFeedFieldProps) => {
	const [fieldValue, setFieldValue] = useState(
		props.value !== null ? Number(props.value).toFixed(0) : '',
	)
	const dispatch = useAppDispatch()

	const fieldValueHandler = e => {
		let value = e.target.value
		if (props.type === 'number') {
			if (Number(value) < 0) {
				value = ''
			}
			if (Number(value) > props.maxValue) value = props.maxValue
		}
		setFieldValue(value)
	}
	const onChangeHandler = () => {
		if (
			(!props.value && !fieldValue) ||
			Number(props.value) === Number(fieldValue) ||
			fieldValue === ''
		)
			return

		const params: IDataOnChangeHandlerParameters = {
			feedPlanDate: moment(props.feedPlanDate).format('YYYY-MM-DD'),
			feedPlanId: props.planId,
			weight: fieldValue,
			feedGroupId: props.groupId,
		}
		if (isFeedWeightMockEnabled()) return

		dispatch(feedNewPlanFactDataOnChange({ ...params }))
			.unwrap()
			.then(() => {
				dispatch(setNewWeightValue(params))
				dispatch(
					notifyUser({
						title: 'Фактические данные',
						description: 'Данные о весе сохранены',
						isError: false,
					}),
				)
			})
			.catch(() => {
				setFieldValue(
					props.value !== null && props.value !== undefined
						? Number(props.value).toFixed(0)
						: '',
				)
				dispatch(
					notifyUser({
						title: 'Фактические данные',
						description: 'Не удалось сохранить вес',
						isError: true,
					}),
				)
			})
	}
	const inputElem = useRef(null)
	useEffect(() => {
		const handleWheel = e => {
			e.target.blur()
		}
		inputElem.current.addEventListener('wheel', handleWheel)

		return () => {
			if (inputElem.current) {
				inputElem.current.removeEventListener('wheel', handleWheel)
			}
		}
	}, [])

	const enterPressHandler = e => {
		if (e.key === 'Enter') {
			e.target.blur()
		}
		if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
			e.preventDefault()
		}
	}

	const emptyLabel = ''

	const exceptSymbols = ['e', 'E', '+', '-', '.', ',']
	const deviation = Boolean(props.hasWeightDeviation)
	const canEditWeight = !props.isFutureDate && isAfterFactTime(props.factDt)
	const fieldHeight = '40px'
	const hasValue = fieldValue !== '' && fieldValue !== null
	const showUnit = hasValue && props.label === 'кг'
	const paddingRight = deviation ? 28 : showUnit ? 32 : 8
	const textShift = (paddingRight - 8) / 2
	return (
		<Box
			sx={{
				position: 'relative',
				width: '166px',
				height: fieldHeight,
				boxSizing: 'border-box',
			}}
		>
			<TextField
				key={`planFeedField${props.fieldName}${props.groupId}`}
				id={`inputId${props.fieldName}${props.groupId}`}
				ref={inputElem}
				margin='none'
				disabled={!canEditWeight}
				title={
					canEditWeight
						? undefined
						: 'Доступно после фактического времени события'
				}
				placeholder={props.label ? `  ${props.label}` : 'кг'}
				type={props.type ? props.type : 'text'}
				label={emptyLabel}
				value={fieldValue === null ? '' : fieldValue}
				autoComplete={props.autoComplete ? props.autoComplete : 'off'}
				onChange={fieldValueHandler}
				sx={{
					pr: '0px',
					m: 0,
					width: '166px',
					height: fieldHeight,
					'& .MuiOutlinedInput-root': {
						height: fieldHeight,
						backgroundColor: deviation ? COLORS.deviation : 'transparent',
						borderRadius: '12px',
					},
					'& .MuiOutlinedInput-root:hover': {
						backgroundColor: deviation ? COLORS.deviation : 'transparent',
					},
					'& .MuiOutlinedInput-notchedOutline': {
						pr: '0px',
						borderWidth: '1px',
						top: 0,
						borderColor:
							canEditWeight && props.metaFlags === true && fieldValue === ''
								? COLORS.health
								: COLORS.secondaryFontOpacity,
					},
					'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
						{
							border: `2px solid ${COLORS.main}`,
						},
					'& .MuiOutlinedInput-root.Mui-focused:hover .MuiOutlinedInput-notchedOutline':
						{
							border: `2px solid ${COLORS.main}`,
						},
					'& .MuiOutlinedInput-input': {
						backgroundColor: 'transparent',
						color: COLORS.black,
						'&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
							WebkitAppearance: 'none',
						},
					},
					'& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
				}}
				onBlur={onChangeHandler}
				onKeyPress={enterPressHandler}
				onKeyDown={e => {
					exceptSymbols.includes(e.key) && e.preventDefault()
					if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
						e.preventDefault()
					}
				}}
				variant='outlined'
				InputProps={{
					endAdornment:
						fieldValue !== '' ? (
							props.label === '%' ? (
								<TypographyPrimary
									sx={{
										position: 'absolute',
										ml: '45px',
										pb: '6px',
										color: COLORS.icon,
									}}
								>
									%
								</TypographyPrimary>
							) : (
								<></>
							)
						) : (
							<></>
						),
					inputProps: {
						style: {
							paddingLeft: '8px',
							paddingRight: `${paddingRight}px`,
							paddingTop: 0,
							paddingBottom: 0,
							height: fieldHeight,
							lineHeight: fieldHeight,
							textAlign: 'center',
							width: '100%',
							boxSizing: 'border-box',
						},
					},
					style: {
						display: 'flex',
						justifyContent: 'center',
						height: fieldHeight,
						paddingRight: '0px',
						fontSize: '14px',
						lineHeight: '17px',
						textAlign: 'right',
						fontWeight: 400,
						color: COLORS.black,
						borderRadius: '12px',
						backgroundColor: deviation ? COLORS.deviation : 'transparent',
					},
				}}
				InputLabelProps={{
					shrink: false,
					style: {
						textAlign: 'right',
						fontSize: '14px',
						lineHeight: '17px',
						fontWeight: 400,
						color: COLORS.icon,
					},
				}}
			/>
			{showUnit && (
				<TypographyPrimary
					sx={{
						position: 'absolute',
						left: '50%',
						top: '50%',
						marginLeft: `calc(-${textShift}px + ${String(fieldValue).length / 2}ch + 4px)`,
						transform: 'translateY(-50%)',
						color: COLORS.icon,
						fontSize: '14px',
						lineHeight: '17px',
						fontWeight: 400,
						pointerEvents: 'none',
						whiteSpace: 'nowrap',
					}}
				>
					кг
				</TypographyPrimary>
			)}
			{deviation && (
				<Box
					sx={{
						position: 'absolute',
						right: '12px',
						top: '50%',
						transform: 'translateY(-50%) rotate(180deg)',
						display: 'flex',
						pointerEvents: 'none',
					}}
				>
					<DeviationLogo />
				</Box>
			)}
		</Box>
	)
}
